import React, { useState, useEffect } from 'react';
import { APP_VERSION } from '../../constants/appConstants';
import { getSystemStatus, forceEmailSync } from '../../services/apiService';
import { X, Mail, Activity, AlertCircle, RefreshCw, Brain, Coins } from 'lucide-react';

const StatusBar = ({ show }) => {
    const [status, setStatus] = useState({ active: false, message: 'Inicializando...', lastError: null, processedToday: 0, recentLogs: [] });
    const [deepseekInfo, setDeepseekInfo] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(true);
    const [showLogs, setShowLogs] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const formatTokens = (num) => {
        if (num === undefined || num === null) return '0';
        if (num >= 1000000) {
            return `${(num / 1000000).toFixed(2).replace('.', ',')}M`;
        }
        if (num >= 1000) {
            return `${(num / 1000).toFixed(1).replace('.', ',')}k`;
        }
        return num.toLocaleString('pt-BR');
    };

    const formatBalance = (balance) => {
        if (!balance) return 'N/A';
        const amount = parseFloat(balance.totalBalance);
        if (isNaN(amount)) return 'N/A';
        const formattedAmount = amount.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        const currencySymbol = balance.currency === 'CNY' ? '¥' : '$';
        if (!balance.isAvailable) {
            return <span className="text-red-500 dark:text-red-400 font-bold">{currencySymbol} {formattedAmount} (Sem Saldo)</span>;
        }
        return `${currencySymbol} ${formattedAmount}`;
    };

    const handleForceSync = async () => {
        setSyncing(true);
        try {
            await forceEmailSync();
            // Optional: You could add a toast notification here if you have a toast system
            // We just wait a bit and fetch status to show the log
            setTimeout(async () => {
                const data = await getSystemStatus();
                if (data && data.success) {
                    setStatus(data.emailStatus || { active: false, message: 'Inicializando...', lastError: null });
                    if (data.deepseek) {
                        setDeepseekInfo(data.deepseek);
                    }
                }
                setSyncing(false);
            }, 2000);
        } catch (error) {
            console.error(error);
            setSyncing(false);
        }
    };

    useEffect(() => {
        if (!show) return;

        const fetchStatus = async () => {
            try {
                const data = await getSystemStatus();
                if (data && data.success) {
                    setStatus(data.emailStatus || { active: false, message: 'Inicializando...', lastError: null });
                    if (data.deepseek) {
                        setDeepseekInfo(data.deepseek);
                    }
                } else {
                     setStatus(prev => ({ ...prev, active: false, message: 'Falha na resposta da API', lastError: 'API Error' }));
                }
            } catch (error) {
                // Se o erro for 401/403 (logout), não mostramos erro de conexão para não poluir
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    setStatus(prev => ({ ...prev, active: false, message: 'Autenticação necessária', lastError: null }));
                } else {
                    setStatus(prev => ({ ...prev, active: false, message: 'Erro de conexão', lastError: error.message }));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();
        const interval = setInterval(fetchStatus, 30000); // Update every 30s
        return () => clearInterval(interval);
    }, [show]);

    if (!show) return null;

    const formatTime = (isoString) => {
        try {
            return new Date(isoString).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        } catch {
            return '';
        }
    };

    return (
        <>
            <div className="fixed bottom-0 left-0 right-0 z-[50] backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-t border-slate-200 dark:border-slate-700 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] px-4 py-1.5 flex flex-wrap justify-between items-center gap-y-1.5 text-xs sm:text-sm text-slate-600 dark:text-slate-300 transition-all duration-300">
                <div className="flex items-center space-x-2 sm:space-x-4">
                    <span className="font-semibold text-slate-500 dark:text-slate-400 opacity-70 hover:opacity-100 transition-opacity">v{APP_VERSION}</span>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span className="opacity-70 text-[10px] sm:text-sm whitespace-nowrap">© {new Date().getFullYear()} Fernando J. Cordeiro</span>
                </div>

                {deepseekInfo && (
                    <div className="flex items-center space-x-2.5 text-[11px] sm:text-xs bg-slate-100/80 dark:bg-slate-800/80 px-3 py-0.5 sm:py-1 rounded-full border border-slate-200/50 dark:border-slate-700/50 shadow-inner">
                        <span className="flex items-center space-x-1 font-medium text-slate-500 dark:text-slate-400">
                            <Brain className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400 animate-pulse" />
                            <span className="hidden xs:inline">DeepSeek:</span>
                        </span>
                        <span className="text-slate-700 dark:text-slate-300 font-semibold" title="Tokens consumidos nos últimos 30 dias">
                            {formatTokens(deepseekInfo.tokensLast30Days)} <span className="text-[10px] font-normal text-slate-400">tokens (30d)</span>
                        </span>
                        <span className="text-slate-300 dark:text-slate-700">|</span>
                        <span className="flex items-center font-semibold text-slate-700 dark:text-slate-300" title="Saldo disponível">
                            <Coins className="w-3.5 h-3.5 mr-1 text-amber-500 dark:text-amber-400" />
                            {formatBalance(deepseekInfo.balance)}
                        </span>
                    </div>
                )}

                <div className="flex items-center space-x-3">
                    <span className="hidden sm:inline opacity-80">Notificações:</span>
                    <button
                        onClick={() => setShowLogs(true)}
                        className="flex items-center space-x-2 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        title="Ver logs da integração"
                        aria-label="Abrir logs do monitoramento de notificações"
                    >
                        <div className="relative flex items-center justify-center w-2.5 h-2.5">
                            {status.active ? (
                                <>
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                </>
                            ) : (
                                <>
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                </>
                            )}
                        </div>
                        <span className={`font-medium truncate max-w-[200px] sm:max-w-xs ${status.active ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                            {status.active ? "Ativo" : (status.message || status.lastError || 'Desconhecido')}
                        </span>
                    </button>
                </div>
            </div>

            {/* Modal de Logs */}
            {showLogs && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-700">
                        <div className="flex justify-between items-center p-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                            <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center">
                                <Activity className="w-5 h-5 mr-2 text-blue-500" />
                                Logs da Integração de Notificações
                            </h2>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handleForceSync}
                                    disabled={syncing || !status.active}
                                    className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                                        syncing || !status.active
                                            ? 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 cursor-not-allowed'
                                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
                                    }`}
                                    title="Forçar sincronização de e-mails não lidos"
                                >
                                    <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                                    <span className="hidden sm:inline">{syncing ? 'Sincronizando...' : 'Sincronizar'}</span>
                                </button>
                                <button
                                    onClick={() => setShowLogs(false)}
                                    className="text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 p-1.5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                    aria-label="Fechar"
                                    title="Fechar"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="p-5 flex-1 overflow-y-auto bg-slate-50/50 dark:bg-slate-900/50">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center space-x-4">
                                    <div className={`p-3 rounded-full ${status.active ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`}>
                                        <Activity className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Status do Serviço</p>
                                        <p className="text-lg font-bold text-slate-800 dark:text-white">
                                            {status.active ? 'Online e Monitorando' : 'Offline / Erro'}
                                        </p>
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center space-x-4">
                                    <div className="p-3 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                                        <Mail className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Novas Hoje (24h)</p>
                                        <p className="text-lg font-bold text-slate-800 dark:text-white">
                                            {status.processedToday || 0}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center">
                                    <h3 className="font-semibold text-slate-700 dark:text-slate-300">Atividade Recente</h3>
                                </div>
                                <div className="divide-y divide-slate-100 dark:divide-slate-700/50 max-h-[300px] overflow-y-auto p-2">
                                    {(!status.recentLogs?.length && !status.recentErrors?.length) ? (
                                        <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                                            <p>Nenhuma atividade recente registrada.</p>
                                        </div>
                                    ) : (
                                        [...(status.recentLogs || []), ...(status.recentErrors || [])]
                                            .filter((v, i, a) => a.findIndex(t => t.timestamp === v.timestamp && t.message === v.message) === i)
                                            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                                            .slice(0, 50)
                                            .map((log, i) => (
                                                <div key={i} className="p-3 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors rounded-lg flex space-x-3 items-start">
                                                    <div className="mt-0.5 flex-shrink-0">
                                                        {log.type === 'error' ? (
                                                            <AlertCircle className="w-4 h-4 text-red-500" />
                                                        ) : (
                                                            <Mail className="w-4 h-4 text-emerald-500" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`text-sm ${log.type === 'error' ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-700 dark:text-slate-300'}`}>
                                                            {log.message}
                                                        </p>
                                                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                                            {formatTime(log.timestamp)}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StatusBar;
